import { experienceUrl } from '@/lib/urls';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Gift, Search, Star, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import ConsumerLayout from '@/components/ConsumerLayout';
import { useLocation } from '@/contexts/LocationContext';
import api from '@/lib/api';

export default function ExperiencesByVibe() {
  const { vibe } = useParams();
  const { currentCity } = useLocation();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const decodedVibe = decodeURIComponent(vibe || '').replace(/-/g, ' ');
  const isExclusive = vibe === 'eventgoers-exclusive';

  useEffect(() => {
    const fetch = async () => {
      try {
        const cityParam = currentCity?.city ? `?city=${currentCity.city}` : '';
        const res = await api.get(`/discover/experiences${cityParam}`);
        let all = res.data.experiences || [];
        if (isExclusive) {
          all = all.filter(e => e.is_eventgoers_exclusive);
        } else {
          all = all.filter(e => (e.vibe_tags || []).some(t => t.toLowerCase() === decodedVibe.toLowerCase()));
        }
        setExperiences(all);
      } catch { setExperiences([]); }
      finally { setLoading(false); }
    };
    fetch();
  }, [vibe, currentCity]);

  const filtered = experiences.filter(e => !search || e.title?.toLowerCase().includes(search.toLowerCase()));
  const title = isExclusive ? 'EVENT★GOERS Exclusive Experiences' : `${decodedVibe} Experiences`;

  return (
    <ConsumerLayout darkTheme>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <Link to="/experiences" className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> All Experiences</Link>
        <Badge className="mb-4 bg-purple-600/20 text-purple-300 border-purple-500/30 text-lg px-4 py-1">{isExclusive ? 'EXCLUSIVE' : decodedVibe}</Badge>
        <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
        <p className="text-slate-400 mb-8">{filtered.length} experience{filtered.length !== 1 ? 's' : ''} in {currentCity?.display || 'your area'}</p>

        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-12" />
        </div>

        {loading ? <div className="text-center py-20 text-slate-400">Loading...</div> : filtered.length === 0 ? (
          <div className="text-center py-20"><Gift className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">No {isExclusive ? 'exclusive' : decodedVibe.toLowerCase()} experiences found in {currentCity?.display || 'your area'}.</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(exp => (
              <Link key={exp.id} to={experienceUrl(exp)}>
                <Card className="bg-slate-800/50 border-slate-700 hover:border-purple-500 transition-all overflow-hidden group h-full">
                  <div className="aspect-video bg-slate-700 overflow-hidden relative">
                    {exp.images?.[0] ? <img src={exp.images[0]} alt={exp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    : <div className="w-full h-full flex items-center justify-center"><Gift className="w-12 h-12 text-slate-600" /></div>}
                    {isExclusive && <Badge className="absolute top-2 right-2 bg-indigo-600 text-white">EXCLUSIVE</Badge>}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-white text-lg line-clamp-1">{exp.title}</h3>
                    {exp.hook && <p className="text-sm text-slate-400 mt-1 italic line-clamp-1">{exp.hook}</p>}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-purple-400 font-bold">${exp.price}</span>
                      <span className="text-xs text-slate-500">{exp.venue_name}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ConsumerLayout>
  );
}
